import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, DollarSign, Clock, TrendingUp, TrendingDown, ArrowRight, ArrowLeft,
  Plus, X, ArrowUpDown, Search, Users, BarChart3, Tag, Eye, ExternalLink,
  CheckCircle, Percent, ShieldCheck, AlertTriangle, Activity,
} from "lucide-react";
import { LuxuryBackground, Card, PageHero, GoldButton } from "../components/shared/UIComponents";
import { useAuth } from "../context/AuthContext";
import { fmtUSD } from "../utils/currency";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecondaryMarketPage() {
  const { user } = useAuth();
  const { navigateToEntity } = usePageNavigate();
  const [listings, setListings] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [myCommitments, setMyCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("browse"); // browse | my-listings | transactions | analytics
  const [myListings, setMyListings] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [marketAnalytics, setMarketAnalytics] = useState(null);
  const [showListModal, setShowListModal] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [buyingId, setBuyingId] = useState(null);
  const [legalStatus, setLegalStatus] = useState(null);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isInvestor = user?.role === "investor" || user?.role === "admin";

  useEffect(() => {
    if (token) {
      axios.get(`${API}/capital/liquidity/legal-status`, { headers })
        .then(r => setLegalStatus(r.data))
        .catch(() => setLegalStatus({ enabled: false, message: "Unable to check secondary market status.", disclosures: [], restrictions: [] }));
    }
  }, []);

  const fetchListings = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/capital/liquidity/listings?sort_by=${sort}`, { headers }),
      isInvestor ? axios.get(`${API}/investor/dashboard`, { headers }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
    ]).then(([listRes, dashRes]) => {
      setListings(listRes.data.listings || []);
      setAnalytics(listRes.data.analytics || {});
      setMyCommitments(dashRes.data.my_commitments || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [sort]);

  useEffect(fetchListings, [sort]);

  const fetchMyListings = useCallback(() => {
    axios.get(`${API}/capital/liquidity/my-listings`, { headers }).then(r => setMyListings(r.data)).catch(console.error);
  }, []);

  const fetchTransactions = useCallback(() => {
    axios.get(`${API}/capital/liquidity/transactions`, { headers }).then(r => setTransactions(r.data)).catch(console.error);
  }, []);

  const fetchMarketAnalytics = useCallback(() => {
    axios.get(`${API}/capital/liquidity/analytics`, { headers }).then(r => setMarketAnalytics(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (activeView === "my-listings") fetchMyListings();
    if (activeView === "transactions") fetchTransactions();
    if (activeView === "analytics") fetchMarketAnalytics();
  }, [activeView]);

  const handleBuy = async (listingId) => {
    if (!window.confirm("Confirm purchase of this position? Funds will be deducted from your wallet.")) return;
    setBuyingId(listingId);
    try {
      await axios.post(`${API}/capital/liquidity/buy`, { listing_id: listingId }, { headers });
      fetchListings();
    } catch (e) {
      alert(e.response?.data?.detail || "Purchase failed");
    } finally {
      setBuyingId(null);
    }
  };

  const handleList = async (commitmentId, askingPrice, termMonths, notes) => {
    try {
      await axios.post(`${API}/capital/liquidity/list`, {
        commitment_id: commitmentId,
        asking_price: parseFloat(askingPrice),
        remaining_term_months: parseInt(termMonths) || null,
        notes: notes || null,
      }, { headers });
      setShowListModal(false);
      fetchListings();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to list position");
    }
  };

  const handleCancel = async (listingId) => {
    if (!window.confirm("Cancel this listing?")) return;
    try {
      await axios.delete(`${API}/capital/liquidity/listing/${listingId}`, { headers });
      fetchMyListings();
      fetchListings();
    } catch (e) { alert(e.response?.data?.detail || "Failed to cancel"); }
  };

  const filtered = listings.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.property_address || "").toLowerCase().includes(q) ||
           (l.loan_type_label || "").toLowerCase().includes(q) ||
           (l.state || "").toLowerCase().includes(q);
  });

  const listableCommitments = myCommitments.filter(c => c.status === "confirmed" || c.status === "funded");

  const TABS = [
    { id: "browse", label: "Browse Listings", icon: Search },
    ...(isInvestor ? [
      { id: "my-listings", label: "My Listings", icon: Tag },
      { id: "transactions", label: "Transactions", icon: Activity },
    ] : []),
    { id: "analytics", label: "Market Analytics", icon: BarChart3 },
  ];

  return (
    <div className="relative" data-testid="secondary-market-page">
      <PageHero eyebrow="Capital Markets" title="Secondary Market"
        description="Trade commitment positions with other investors. Access liquidity for your portfolio with full 360 transparency." />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">

          {/* Legal Gate Banner */}
          {legalStatus && !legalStatus.enabled && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="legal-gate-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Secondary Market — Under Legal Review</p>
                  <p className="text-xs text-amber-700 mt-1">{legalStatus.message}</p>
                  {legalStatus.disclosures?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {legalStatus.disclosures.map((d, i) => (
                        <li key={i} className="text-[10px] text-amber-600 flex items-start gap-1.5">
                          <span className="text-amber-400 mt-0.5">-</span> {d}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[10px] text-amber-500 mt-2 font-medium">Listing, purchasing, and transfer actions are disabled. Browsing historical data and analytics is permitted.</p>
                </div>
              </div>
            </div>
          )}

          {/* Market Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Active Listings", value: listings.length, icon: ArrowUpDown, color: "text-emerald-600" },
              { label: "Total Volume", value: fmtUSD(analytics.total_volume || 0), icon: DollarSign, color: "text-gold-dark" },
              { label: "Avg Yield", value: analytics.avg_yield > 0 ? `${analytics.avg_yield}%` : "—", icon: TrendingUp, color: "text-blue-600" },
              { label: "Active Sellers", value: analytics.seller_count || 0, icon: Users, color: "text-purple-600" },
            ].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                <Card hover={false}>
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-gold/10 p-2.5 border border-cream-warm"><s.icon className={`h-5 w-5 ${s.color}`} /></div>
                    <div>
                      <div className="text-2xl font-semibold text-[#0A0A0A]">{s.value}</div>
                      <div className="text-xs text-[#888]">{s.label}</div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Tab Nav */}
          <div className="flex items-center gap-2 mb-6 border-b border-cream-warm pb-3 overflow-x-auto">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveView(tab.id)}
                data-testid={`market-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                  activeView === tab.id
                    ? "bg-emerald-700 text-white"
                    : "text-[#888] hover:text-[#333] hover:bg-cream"
                }`}>
                <tab.icon className="h-4 w-4" /> {tab.label}
              </button>
            ))}
            <div className="flex-1" />
            {isInvestor && listableCommitments.length > 0 && (
              <button onClick={() => setShowListModal(true)} data-testid="list-position-btn"
                className="px-4 py-2 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 transition-colors flex items-center gap-1.5">
                <Plus className="h-4 w-4" /> List Position
              </button>
            )}
          </div>

          {/* Browse View */}
          {activeView === "browse" && (
            <>
              <div className="flex flex-wrap gap-3 mb-6 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by property, loan type, state..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-cream-warm bg-white text-sm focus:outline-none focus:border-emerald-300"
                    data-testid="secondary-search" />
                </div>
                <select value={sort} onChange={e => setSort(e.target.value)}
                  className="px-3 py-2.5 rounded-xl border border-cream-warm bg-white text-sm" data-testid="secondary-sort">
                  <option value="newest">Newest First</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                  <option value="yield">Highest Yield</option>
                  <option value="discount">Best Discount</option>
                </select>
              </div>

              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
              ) : filtered.length === 0 ? (
                <Card hover={false}>
                  <div className="text-center py-12 text-[#999]">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-20" />
                    <p className="text-lg mb-2">No Active Listings</p>
                    <p className="text-sm">When investors list commitment positions for sale, they'll appear here.</p>
                  </div>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {filtered.map((l, i) => (
                    <motion.div key={l.listing_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                      <ListingCard listing={l} user={user} onBuy={handleBuy}
                        buying={buyingId === l.listing_id}
                        onEntityNavigate={navigateToEntity} />
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* My Listings View */}
          {activeView === "my-listings" && (
            <MyListingsView data={myListings} onCancel={handleCancel}
              onEntityNavigate={navigateToEntity} onRefresh={fetchMyListings} />
          )}

          {/* Transactions View */}
          {activeView === "transactions" && (
            <TransactionsView data={transactions} onEntityNavigate={navigateToEntity} />
          )}

          {/* Analytics View */}
          {activeView === "analytics" && (
            <AnalyticsView data={marketAnalytics} />
          )}
        </section>
      </div>

      {/* List Position Modal */}
      <AnimatePresence>
        {showListModal && (
          <ListPositionModal commitments={listableCommitments}
            onList={handleList} onClose={() => setShowListModal(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Listing Card ────────────────────────────────────────────
function ListingCard({ listing: l, user, onBuy, buying, onEntityNavigate }) {
  const isOwner = l.seller_id === user?.user_id;
  const priceDelta = l.price_change_pct || 0;

  return (
    <Card data-testid={`listing-card-${l.listing_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-sm font-bold text-[#222]">{l.property_address || `Position ${(l.listing_id || "").slice(0, 8)}`}</h4>
          <div className="flex items-center gap-2 text-[10px] text-[#888] mt-0.5">
            {l.loan_type_label && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{l.loan_type_label}</span>}
            {l.state && <span>{l.state}</span>}
            {l.deal_number && <span className="text-emerald-600">{l.deal_number}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {priceDelta < 0 && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 text-[9px] font-bold">
              <TrendingDown className="h-3 w-3" /> {Math.abs(priceDelta)}% disc
            </span>
          )}
          {priceDelta > 0 && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 text-[9px] font-bold">
              <TrendingUp className="h-3 w-3" /> +{priceDelta}% prem
            </span>
          )}
        </div>
      </div>

      {/* Pricing grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="p-2.5 rounded-lg bg-[#faf7f2] text-center">
          <div className="text-sm font-bold text-[#222]">{fmtUSD(l.original_amount || 0)}</div>
          <div className="text-[9px] text-[#999]">Original</div>
        </div>
        <div className="p-2.5 rounded-lg bg-emerald-50 text-center">
          <div className="text-sm font-bold text-emerald-700">{fmtUSD(l.asking_price || 0)}</div>
          <div className="text-[9px] text-[#999]">Asking</div>
        </div>
        <div className="p-2.5 rounded-lg bg-[#faf7f2] text-center">
          <div className="text-sm font-bold text-[#222]">{l.projected_yield ? `${l.projected_yield}%` : "—"}</div>
          <div className="text-[9px] text-[#999]">Yield</div>
        </div>
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-3 text-[10px] text-[#888] mb-3">
        {l.remaining_term_months && (
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {l.remaining_term_months}mo remaining</span>
        )}
        <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {l.views || 0} views</span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-cream-warm">
        <span className="text-[10px] text-[#bbb]">Listed by {l.seller_name || "Investor"}</span>
        <div className="flex items-center gap-2">
          {/* 360 link */}
          {onEntityNavigate && (
            <button onClick={() => onEntityNavigate("listing", l.listing_id)}
              data-testid={`listing-360-${l.listing_id}`}
              className="px-2 py-1.5 rounded-lg border border-cream-warm text-[10px] font-medium text-emerald-700 hover:bg-emerald-50 transition-colors flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> 360
            </button>
          )}
          {!isOwner ? (
            <button onClick={() => onBuy(l.listing_id)}
              disabled={buying}
              data-testid={`buy-listing-${l.listing_id}`}
              className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-1.5 disabled:opacity-50">
              {buying ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3" />} Buy
            </button>
          ) : (
            <span className="text-[10px] text-emerald-600 font-semibold">Your Listing</span>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── My Listings View ────────────────────────────────────────
function MyListingsView({ data, onCancel, onEntityNavigate, onRefresh }) {
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;

  const { listings, active_count, sold_count, total_sold_volume } = data;

  return (
    <div className="space-y-6" data-testid="my-listings-view">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card hover={false}><div className="text-center"><div className="text-2xl font-bold text-[#0A0A0A]">{active_count}</div><div className="text-xs text-[#888]">Active</div></div></Card>
        <Card hover={false}><div className="text-center"><div className="text-2xl font-bold text-emerald-600">{sold_count}</div><div className="text-xs text-[#888]">Sold</div></div></Card>
        <Card hover={false}><div className="text-center"><div className="text-2xl font-bold text-gold-dark">{fmtUSD(total_sold_volume || 0)}</div><div className="text-xs text-[#888]">Volume Sold</div></div></Card>
      </div>

      {listings.length === 0 ? (
        <Card hover={false}><div className="text-center py-12 text-[#999]"><Tag className="h-12 w-12 mx-auto mb-3 opacity-20" /><p>You haven't listed any positions yet.</p></div></Card>
      ) : (
        <div className="space-y-3">
          {listings.map(l => (
            <Card key={l.listing_id} hover={false}>
              <div className="flex items-center justify-between" data-testid={`my-listing-${l.listing_id}`}>
                <div className="flex items-center gap-4">
                  <div className={`p-2.5 rounded-xl border ${l.status === "active" ? "bg-emerald-50 border-emerald-200" : l.status === "sold" ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"}`}>
                    <DollarSign className={`h-5 w-5 ${l.status === "active" ? "text-emerald-600" : l.status === "sold" ? "text-blue-600" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0A0A0A]">{l.property_address || `Position ${(l.listing_id || "").slice(0, 8)}`}</div>
                    <div className="flex items-center gap-2 text-xs text-[#888]">
                      {l.loan_type_label && <span>{l.loan_type_label}</span>}
                      {l.state && <><span>-</span><span>{l.state}</span></>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-lg font-bold text-[#0A0A0A]">{fmtUSD(l.asking_price || 0)}</div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      l.status === "active" ? "bg-emerald-100 text-emerald-700" :
                      l.status === "sold" ? "bg-blue-100 text-blue-700" :
                      "bg-gray-100 text-gray-500"
                    }`}>{l.status.toUpperCase()}</span>
                  </div>
                  <div className="flex gap-2">
                    {onEntityNavigate && (
                      <button onClick={() => onEntityNavigate("listing", l.listing_id)}
                        className="px-2 py-1.5 rounded-lg border border-cream-warm text-[10px] font-medium text-emerald-700 hover:bg-emerald-50 transition-colors">
                        360
                      </button>
                    )}
                    {l.status === "active" && (
                      <button onClick={() => onCancel(l.listing_id)}
                        className="px-3 py-1.5 rounded-lg border border-red-200 text-xs text-red-600 hover:bg-red-50 transition-colors">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Transactions View ───────────────────────────────────────
function TransactionsView({ data, onEntityNavigate }) {
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;

  return (
    <div className="space-y-6" data-testid="transactions-view">
      <div className="grid grid-cols-2 gap-4">
        <Card hover={false}><div className="text-center"><div className="text-2xl font-bold text-[#0A0A0A]">{data.total}</div><div className="text-xs text-[#888]">Total Transactions</div></div></Card>
        <Card hover={false}><div className="text-center"><div className="text-2xl font-bold text-gold-dark">{fmtUSD(data.total_volume || 0)}</div><div className="text-xs text-[#888]">Total Volume</div></div></Card>
      </div>

      {data.transactions.length === 0 ? (
        <Card hover={false}><div className="text-center py-12 text-[#999]"><Activity className="h-12 w-12 mx-auto mb-3 opacity-20" /><p>No transactions yet.</p></div></Card>
      ) : (
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Transaction History</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cream-warm text-[10px] uppercase tracking-wider text-[#999]">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Property</th>
                  <th className="text-left py-2">Type</th>
                  <th className="text-right py-2">Amount</th>
                  <th className="text-right py-2">Change</th>
                  <th className="text-left py-2">Role</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((t, i) => (
                  <tr key={t.transaction_id || i} className="border-b border-cream-warm/50 hover:bg-cream/50 transition-colors">
                    <td className="py-2.5 text-[#666] text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                    <td className="py-2.5 font-medium text-[#333] max-w-[180px] truncate">{t.property_address || "Position"}</td>
                    <td className="py-2.5"><span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700">{t.loan_type_label || "—"}</span></td>
                    <td className="py-2.5 text-right font-semibold text-[#0A0A0A]">${(t.sale_price || 0).toLocaleString()}</td>
                    <td className="py-2.5 text-right">
                      <span className={`text-xs font-medium ${t.price_change_pct < 0 ? "text-emerald-600" : t.price_change_pct > 0 ? "text-amber-600" : "text-[#888]"}`}>
                        {t.price_change_pct > 0 ? "+" : ""}{t.price_change_pct}%
                      </span>
                    </td>
                    <td className="py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${t.is_buyer ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {t.is_buyer ? "BUYER" : "SELLER"}
                      </span>
                    </td>
                    <td className="py-2.5">
                      {onEntityNavigate && (
                        <button onClick={() => onEntityNavigate("listing", t.listing_id)}
                          className="text-[10px] text-emerald-600 hover:text-emerald-700 font-medium">View 360</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Analytics View ──────────────────────────────────────────
function AnalyticsView({ data }) {
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;

  const { market_summary, pricing, loan_type_distribution, recent_transactions } = data;

  return (
    <div className="space-y-6" data-testid="analytics-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Active Listings", value: market_summary.active_listings, color: "text-emerald-600" },
          { label: "Active Volume", value: fmtUSD(market_summary.active_volume || 0), color: "text-gold-dark" },
          { label: "Total Transactions", value: market_summary.total_transactions, color: "text-blue-600" },
          { label: "Completed Volume", value: fmtUSD(market_summary.completed_volume || 0), color: "text-purple-600" },
        ].map(s => (
          <Card key={s.label} hover={false}>
            <div className="text-center"><div className={`text-2xl font-bold ${s.color}`}>{s.value}</div><div className="text-xs text-[#888]">{s.label}</div></div>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Pricing Analysis */}
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Pricing Analysis</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-cream-warm">
              <span className="text-sm text-[#666]">Listed at Discount</span>
              <span className="text-sm font-bold text-emerald-600">{pricing.listings_at_discount} ({pricing.avg_discount}% avg)</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-cream-warm">
              <span className="text-sm text-[#666]">Listed at Premium</span>
              <span className="text-sm font-bold text-amber-600">{pricing.listings_at_premium} (+{pricing.avg_premium}% avg)</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-[#666]">Listed at Par</span>
              <span className="text-sm font-bold text-[#333]">{pricing.listings_at_par}</span>
            </div>
          </div>
        </Card>

        {/* Loan Type Distribution */}
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">By Loan Type</h4>
          {Object.entries(loan_type_distribution).length === 0 ? (
            <div className="text-center py-6 text-sm text-[#999]">No data yet</div>
          ) : (
            <div className="space-y-3">
              {Object.entries(loan_type_distribution).map(([type, count], i) => {
                const total = Object.values(loan_type_distribution).reduce((s, v) => s + v, 0);
                const pct = ((count / total) * 100).toFixed(0);
                const colors = ["bg-emerald-500", "bg-blue-500", "bg-amber-500", "bg-purple-500"];
                return (
                  <div key={type}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-[#333] font-medium">{type}</span>
                      <span className="text-[#666]">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2 rounded-full bg-cream-warm overflow-hidden">
                      <div className={`h-full rounded-full ${colors[i % colors.length]}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Market Participants */}
      <div className="grid grid-cols-2 gap-4">
        <Card hover={false}><div className="text-center"><div className="text-3xl font-bold text-emerald-600">{market_summary.unique_sellers}</div><div className="text-xs text-[#888]">Unique Sellers</div></div></Card>
        <Card hover={false}><div className="text-center"><div className="text-3xl font-bold text-blue-600">{market_summary.unique_buyers}</div><div className="text-xs text-[#888]">Unique Buyers</div></div></Card>
      </div>
    </div>
  );
}

// ── List Position Modal ─────────────────────────────────────
function ListPositionModal({ commitments, onList, onClose }) {
  const [selected, setSelected] = useState("");
  const [askingPrice, setAskingPrice] = useState("");
  const [termMonths, setTermMonths] = useState("");
  const [notes, setNotes] = useState("");

  const commit = commitments.find(c => c.commitment_id === selected);

  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-[#e8e0d0] p-6"
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} data-testid="list-position-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#222]">List Position for Sale</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-cream"><X className="h-4 w-4 text-[#888]" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-[#555] mb-1 block">Select Commitment</label>
            <select value={selected} onChange={e => {
              setSelected(e.target.value);
              const c = commitments.find(x => x.commitment_id === e.target.value);
              if (c) setAskingPrice(c.amount);
            }} className="w-full px-3 py-2.5 rounded-xl border border-cream-warm bg-white text-sm" data-testid="select-commitment">
              <option value="">Choose a commitment...</option>
              {commitments.map(c => (
                <option key={c.commitment_id} value={c.commitment_id}>
                  {fmtUSD(c.amount)} — {c.status} ({(c.commitment_id || "").slice(0, 8)})
                </option>
              ))}
            </select>
          </div>

          {commit && (
            <>
              <div className="rounded-xl bg-cream border border-cream-warm p-3">
                <div className="text-xs text-[#888] mb-1">Original Investment</div>
                <div className="text-lg font-bold text-[#222]">{fmtUSD(commit.amount)}</div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#555] mb-1 block">Asking Price ($)</label>
                <input type="number" value={askingPrice} onChange={e => setAskingPrice(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-cream-warm text-sm" data-testid="asking-amount" />
                {commit.amount && askingPrice && (
                  <div className={`text-[10px] mt-1 ${parseFloat(askingPrice) < commit.amount ? "text-emerald-600" : parseFloat(askingPrice) > commit.amount ? "text-amber-600" : "text-[#888]"}`}>
                    {parseFloat(askingPrice) < commit.amount
                      ? `${((1 - parseFloat(askingPrice) / commit.amount) * 100).toFixed(1)}% discount`
                      : parseFloat(askingPrice) > commit.amount
                        ? `${((parseFloat(askingPrice) / commit.amount - 1) * 100).toFixed(1)}% premium`
                        : "At par"}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-bold text-[#555] mb-1 block">Remaining Term (months, optional)</label>
                <input type="number" value={termMonths} onChange={e => setTermMonths(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-cream-warm text-sm" data-testid="remaining-term" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#555] mb-1 block">Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-cream-warm text-sm h-16 resize-none" placeholder="Reason for selling, urgency, etc." />
              </div>
              <GoldButton onClick={() => onList(selected, askingPrice, termMonths, notes)} data-testid="confirm-list-btn"
                disabled={!askingPrice || parseFloat(askingPrice) <= 0}>
                List for {fmtUSD(parseFloat(askingPrice || 0))}
              </GoldButton>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
